import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
	Button,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Table,
	TableHead,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
	Avatar,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	TextField,
	InputAdornment,
} from "@material-ui/core";
import {
	Edit,
	CheckCircle,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
	Cloud,
	PhoneAndroid,
	Email,
	Instagram,
	Sms,
	Link as LinkIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import ConnectionLogsModal from "../../components/ConnectionLogsModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import useSettings from "../../hooks/useSettings";
import usePlans from "../../hooks/usePlans";
import { startInstagramLogin } from "../../utils/instagramOAuth";

import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { canShowMenu } from "../../helpers/menuAccess";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	tooltip: {
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.text.primary,
		fontSize: theme.typography.pxToRem(14),
		border: `1px solid ${theme.palette.divider}`,
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
}));

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

// Canal de QR Code = tudo que não é Cloud API, Email, Instagram ou SMS
// (type vazio em conexão antiga também é QR Code).
const isQrChannel = type => !["cloudapi", "email", "instagram", "sms"].includes(type);

// Estado real de uma conexão Instagram — o `status` dela é sempre CONNECTED, quem
// diz se funciona é o token e a assinatura dos webhooks:
// "reconnect" = token expirado/revogado; "subscribe" = webhooks não assinados.
// Expiração null não conta: o backend confere o token na Meta e grava "expired".
const getInstagramState = whatsApp => {
	const subscription = whatsApp.instagramSubscriptionStatus;
	const expiresAt = whatsApp.instagramTokenExpiresAt
		? new Date(whatsApp.instagramTokenExpiresAt)
		: null;

	if (
		subscription === "expired" ||
		subscription === "deauthorized" ||
		!whatsApp.instagramUserId ||
		(expiresAt && expiresAt.getTime() <= Date.now())
	) {
		return "reconnect";
	}
	if (subscription === "pending" || subscription === "error") {
		return "subscribe";
	}
	return "connected";
};

const Connections = () => {
	const classes = useStyles();

	const { user, menus } = useContext(AuthContext);
	const { whatsApps, loading } = useContext(WhatsAppsContext);
	// Adicionar, ler QR, editar e excluir conexões: admin sempre; demais perfis só
	// com "conexoes_gerenciar" marcado em Perfis. A chave "conexoes" sozinha só abre a tela.
	const canManage = canShowMenu(user, menus, "conexoes_gerenciar");
	const socketManager = useContext(SocketContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [loadingSession, setLoadingSession] = useState({});
	const [loadingQrCode, setLoadingQrCode] = useState({});
	const [loadingSubscribe, setLoadingSubscribe] = useState({});
	const [whatsappConnectionType, setWhatsappConnectionType] = useState("both");
	// Diálogo de link público / apelido personalizado
	const [linkModalWhatsApp, setLinkModalWhatsApp] = useState(null);
	const [linkData, setLinkData] = useState(null); // { url, customUrl, customSlug, ... }
	const [slugInput, setSlugInput] = useState("");
	const [linkLoading, setLinkLoading] = useState(false);
	const [linkSaving, setLinkSaving] = useState(false);
	// Logs de conexão (oculto — apenas suporte/super via shift+ctrl+click no nome)
	const [logsModalWhatsApp, setLogsModalWhatsApp] = useState(null);
	const { getAll } = useSettings();
	const { getPlanCompany } = usePlans();
	// Plano inclui WhatsApp via QR Code? null = ainda carregando.
	const [allowQrConnection, setAllowQrConnection] = useState(null);

	useEffect(() => {
		const fetchPlan = async () => {
			try {
				const planConfigs = await getPlanCompany(undefined, user.companyId);
				setAllowQrConnection(planConfigs?.plan?.useWhatsappQr !== false);
			} catch (err) {
				// Falha fechado: sem o plano em mãos o canal de QR Code não é
				// oferecido. O bloqueio real é do servidor.
				setAllowQrConnection(false);
			}
		};
		fetchPlan();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Buscar configuração de tipo de conexão
	useEffect(() => {
		const fetchSettings = async () => {
			try {
				const settingsList = await getAll();
				const connTypeSetting = settingsList?.find(s => s.key === "whatsappConnectionType");
				if (connTypeSetting) {
					setWhatsappConnectionType(connTypeSetting.value);
				}
			} catch (err) {
				console.error("Erro ao buscar configuração de tipo de conexão:", err);
			}
		};
		fetchSettings();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Resultado do login facilitado do Instagram (redirect ?instagram=success|error)
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const igResult = params.get("instagram");
		if (!igResult) return;
		if (igResult === "success") {
			const username = params.get("username");
			toast.success(
				username
					? i18n.t("connections.toasts.instagramConnectedAs", { username })
					: i18n.t("connections.toasts.instagramConnected")
			);
		} else if (igResult === "error") {
			toast.error(i18n.t("connections.toasts.instagramFailed", {
				reason: params.get("reason") || i18n.t("connections.toasts.unknownError"),
			}));
		}
		// Limpa os parâmetros da URL
		window.history.replaceState({}, document.title, window.location.pathname);
	}, []);

	// Pareamento bloqueado por número duplicado: o backend recusa a conexão,
	// desvincula o aparelho no WhatsApp e apaga as credenciais. O aviso fica aqui
	// (e não no QrcodeModal) para aparecer também quando o popup já foi fechado —
	// o modal, quando aberto, apenas se fecha ao receber o mesmo evento.
	useEffect(() => {
		const companyId = localStorage.getItem("companyId");
		const socket = socketManager.getSocket(companyId);
		if (!socket) return undefined;

		const handleSessionUpdate = (data) => {
			if (data?.blocked?.reason === "duplicate_connected_number") {
				toast.error(data.blocked.message, { autoClose: 12000 });
			}
		};

		socket.on(`company-${companyId}-whatsappSession`, handleSessionUpdate);
		return () => {
			socket.off(`company-${companyId}-whatsappSession`, handleSessionUpdate);
		};
	}, [socketManager]);

	// Plano sem WhatsApp via QR Code: a configuração "Tipo de Conexão WhatsApp"
	// deixa de valer (o select some das Opções) e a tela se comporta como "both".
	const effectiveConnectionType = allowQrConnection === false ? "both" : whatsappConnectionType;

	// Filtrar conexões pelo tipo permitido
	const filteredWhatsApps = whatsApps?.filter(w => {
		if (w.status === "deleted") return false;
		// Email, SMS e Instagram são canais próprios, sempre visíveis independentemente
		// do tipo de WhatsApp permitido (baileys/cloudapi).
		if (w.type === "email" || w.type === "instagram" || w.type === "sms") return true;
		if (effectiveConnectionType === "both") return true;
		if (effectiveConnectionType === "baileys") return w.type !== "cloudapi";
		if (effectiveConnectionType === "cloudapi") return w.type === "cloudapi";
		return true;
	}) || [];
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		connectionType: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	// Limpar loading quando o status muda para algo que indica progresso
	useEffect(() => {
		whatsApps.forEach(whatsApp => {
			// Só limpa o loading se o status mudou para OPENING, qrcode ou CONNECTED
			const statusChanged = ["OPENING", "qrcode", "CONNECTED"].includes(whatsApp.status);
			
			if (statusChanged) {
				setLoadingSession(prev => {
					if (prev[whatsApp.id]) {
						const newState = { ...prev };
						delete newState[whatsApp.id];
						return newState;
					}
					return prev;
				});
				setLoadingQrCode(prev => {
					if (prev[whatsApp.id]) {
						const newState = { ...prev };
						delete newState[whatsApp.id];
						return newState;
					}
					return prev;
				});
			}
		});
	}, [whatsApps]);

	const handleStartWhatsAppSession = async whatsAppId => {
		try {
			setLoadingSession(prev => ({ ...prev, [whatsAppId]: true }));
			await api.post(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
			// Remove loading apenas em caso de erro
			setLoadingSession(prev => {
				const newState = { ...prev };
				delete newState[whatsAppId];
				return newState;
			});
		}
	};

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			setLoadingQrCode(prev => ({ ...prev, [whatsAppId]: true }));
			await api.put(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
			// Remove loading apenas em caso de erro
			setLoadingQrCode(prev => {
				const newState = { ...prev };
				delete newState[whatsAppId];
				return newState;
			});
		}
	};

	const handleGenerateNewQrCode = async whatsAppId => {
		try {
			setLoadingQrCode(prev => ({ ...prev, [whatsAppId]: true }));
			await api.post(`/whatsappsession/${whatsAppId}/generate-qr`);
		} catch (err) {
			toastError(err);
			// Remove loading apenas em caso de erro
			setLoadingQrCode(prev => {
				const newState = { ...prev };
				delete newState[whatsAppId];
				return newState;
			});
		}
	};

	const handleStartInstagramLogin = async () => {
		try {
			await startInstagramLogin();
		} catch (err) {
			toastError(err);
		}
	};

	const handleSubscribeInstagram = async whatsAppId => {
		try {
			setLoadingSubscribe(prev => ({ ...prev, [whatsAppId]: true }));
			const { data } = await api.post(`/instagram/${whatsAppId}/subscribe`);
			if (data?.success) {
				toast.success(i18n.t("connections.toasts.instagramSubscribed"));
			} else {
				toast.error(i18n.t("connections.toasts.instagramSubscribeFailed"));
			}
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingSubscribe(prev => {
				const newState = { ...prev };
				delete newState[whatsAppId];
				return newState;
			});
		}
	};

	const handleSubscribeCloudApi = async (whatsAppId) => {
		try {
			setLoadingSubscribe(prev => ({ ...prev, [whatsAppId]: true }));
			const { data } = await api.post(`/whatsapp/${whatsAppId}/cloudapi/subscribe`);
			toast.success(data?.message || i18n.t("connections.toasts.subscribed"));
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingSubscribe(prev => {
				const newState = { ...prev };
				delete newState[whatsAppId];
				return newState;
			});
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = async (whatsApp) => {
		// Sempre abre o modal - o próprio modal cuidará de verificar validade e gerar QR se necessário
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	// Acesso oculto do suporte: shift+ctrl+click (ou shift+cmd no mac) no nome da conexão
	const handleConnectionNameClick = (e, whatsApp) => {
		if (e.shiftKey && (e.ctrlKey || e.metaKey) && user?.super) {
			e.preventDefault();
			setLogsModalWhatsApp(whatsApp);
		}
	};

	// Abre o diálogo de link público: busca o link padrão + apelido atual.
	const handleOpenLinkModal = async whatsApp => {
		setLinkModalWhatsApp(whatsApp);
		setLinkData(null);
		setSlugInput("");
		setLinkLoading(true);
		try {
			const { data } = await api.get(`/public-connection/generate-hash/${whatsApp.id}`);
			if (data.success) {
				setLinkData(data.data);
				setSlugInput(data.data.customSlug || "");
			}
		} catch (err) {
			toastError(err);
		} finally {
			setLinkLoading(false);
		}
	};

	const handleCloseLinkModal = () => {
		setLinkModalWhatsApp(null);
		setLinkData(null);
		setSlugInput("");
	};

	const handleCopyText = (text, message) => {
		if (!text) return;
		navigator.clipboard.writeText(text);
		toast.success(message || i18n.t("connections.toasts.linkCopied"));
	};

	// Salva (ou remove, quando vazio) o apelido personalizado da conexão.
	const handleSaveCustomLink = async () => {
		if (!linkModalWhatsApp) return;
		setLinkSaving(true);
		try {
			const { data } = await api.put(
				`/public-connection/custom-link/${linkModalWhatsApp.id}`,
				{ slug: slugInput.trim() }
			);
			if (data.success) {
				setLinkData(prev => ({ ...prev, ...data.data }));
				setSlugInput(data.data.customSlug || "");
				toast.success(
					data.data.customSlug
						? i18n.t("connections.toasts.nicknameSaved")
						: i18n.t("connections.toasts.nicknameRemoved")
				);
			}
		} catch (err) {
			toastError(err);
		} finally {
			setLinkSaving(false);
		}
	};

	const handleOpenConfirmationModal = (action, whatsAppId, connectionType = "") => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				connectionType,
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t(`connections.toasts.deletedByType.${confirmModalInfo.connectionType}`, {
					defaultValue: i18n.t("connections.toasts.deleted"),
				}));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderActionButtons = whatsApp => {
		if (whatsApp.type === "email") {
			return (
				<Button
					size="small"
					variant="outlined"
					disabled
				>
					{i18n.t("connections.buttons.emailChannel")}
				</Button>
			);
		}

		// SMS: gateway HTTP, não tem sessão para conectar/desconectar
		if (whatsApp.type === "sms") {
			return (
				<Button
					size="small"
					variant="outlined"
					disabled
				>
					{i18n.t("connections.buttons.smsChannel")}
				</Button>
			);
		}

		// Instagram: reconectar (login facilitado/OAuth) só com o token inválido
		if (whatsApp.type === "instagram") {
			const instagramState = getInstagramState(whatsApp);

			if (instagramState === "reconnect") {
				return (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={handleStartInstagramLogin}
					>
						{i18n.t("connections.buttons.reconnectInstagram")}
					</Button>
				);
			}

			if (instagramState === "subscribe") {
				return (
					<Button
						size="small"
						variant="outlined"
						color="primary"
						onClick={() => handleSubscribeInstagram(whatsApp.id)}
						disabled={loadingSubscribe[whatsApp.id]}
						startIcon={loadingSubscribe[whatsApp.id] ? <CircularProgress size={20} /> : null}
					>
						{loadingSubscribe[whatsApp.id] ? i18n.t("connections.buttons.subscribing") : i18n.t("connections.buttons.instagramSubscribe")}
					</Button>
				);
			}

			return (
				<Button
					size="small"
					variant="outlined"
					color="default"
					disabled
				>
					{i18n.t("connections.buttons.instagramConnected")}
				</Button>
			);
		}

		// Cloud API não precisa de QR Code ou sessão Baileys
		if (whatsApp.type === "cloudapi") {
			const subscriptionStatus = whatsApp.cloudApiSubscriptionStatus || "pending";
			return (
				<>
					{subscriptionStatus === "pending" && (
						<Button
							size="small"
							variant="contained"
							color="primary"
							onClick={() => handleSubscribeCloudApi(whatsApp.id)}
							disabled={loadingSubscribe[whatsApp.id]}
							startIcon={loadingSubscribe[whatsApp.id] ? <CircularProgress size={20} /> : null}
						>
							{loadingSubscribe[whatsApp.id] ? i18n.t("connections.buttons.subscribing") : i18n.t("connections.buttons.subscribe")}
						</Button>
					)}
					{subscriptionStatus === "subscribed" && (
						<Button
							size="small"
							variant="outlined"
							color="default"
							disabled
						>
							{i18n.t("connections.buttons.subscribed")}
						</Button>
					)}
				</>
			);
		}

		// Baileys - comportamento original
		return (
			<>
				{whatsApp.status === "qrcode" && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}
				{whatsApp.status === "DISCONNECTED" && (
					<>
						{(whatsApp.hasSession || whatsApp.session) && (
							<Button
								size="small"
								variant="contained"
								color="primary"
								onClick={() => handleRequestNewQrCode(whatsApp.id)}
								disabled={loadingQrCode[whatsApp.id]}
								startIcon={loadingQrCode[whatsApp.id] ? <CircularProgress size={20} /> : null}
							>
								{loadingQrCode[whatsApp.id] ? i18n.t("connections.buttons.connecting") : i18n.t("connections.buttons.tryAgain")}
							</Button>
						)}{" "}
						<Button
							size="small"
							variant="outlined"
							color="default"
							onClick={() => handleGenerateNewQrCode(whatsApp.id)}
							disabled={loadingQrCode[whatsApp.id]}
							startIcon={loadingQrCode[whatsApp.id] ? <CircularProgress size={20} /> : null}
						>
							{loadingQrCode[whatsApp.id] ? i18n.t("connections.buttons.connecting") : i18n.t("connections.buttons.newQr")}
						</Button>
					</>
				)}
				{whatsApp.status === "CONNECTED" && (
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => {
							handleOpenConfirmationModal("disconnect", whatsApp.id);
						}}
					>
						{i18n.t("connections.buttons.disconnect")}
					</Button>
				)}
				{whatsApp.status === "OPENING" && (
					<Button 
						size="small" 
						variant="outlined" 
						disabled 
						color="default"
						startIcon={<CircularProgress size={20} />}
					>
						{i18n.t("connections.buttons.connecting")}
					</Button>
				)}

			</>
		);
	};

	const renderStatusToolTips = whatsApp => {
		if (whatsApp.type === "instagram") {
			const instagramState = getInstagramState(whatsApp);
			return (
				<div className={classes.customTableCell}>
					{instagramState === "reconnect" && (
						<CustomToolTip
							title={i18n.t("connections.toolTips.instagramReconnect.title")}
							content={i18n.t("connections.toolTips.instagramReconnect.content")}
						>
							<SignalCellularConnectedNoInternet0Bar color="secondary" />
						</CustomToolTip>
					)}
					{instagramState === "subscribe" && (
						<CustomToolTip
							title={i18n.t("connections.toolTips.instagramSubscribe.title")}
							content={i18n.t("connections.toolTips.instagramSubscribe.content")}
						>
							<SignalCellularConnectedNoInternet2Bar style={{ color: "#f57c00" }} />
						</CustomToolTip>
					)}
					{instagramState === "connected" && (
						<CustomToolTip title={i18n.t("connections.toolTips.CONNECTED.title")}>
							<SignalCellular4Bar style={{ color: green[500] }} />
						</CustomToolTip>
					)}
				</div>
			);
		}

		return (
			<div className={classes.customTableCell}>
				{whatsApp.status === "DISCONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.DISCONNECTED.title")}
						content={
							// Cloud API não tem QR: o motivo vem da Meta (ex.: número em
							// coexistência desconectado no app do celular).
							whatsApp.type === "cloudapi"
								? whatsApp.cloudApiSubscriptionLastError || undefined
								: allowQrConnection
								? i18n.t("connections.toolTips.DISCONNECTED.content")
								: undefined
						}
					>
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{whatsApp.status === "OPENING" && (
					<CircularProgress size={24} className={classes.buttonProgress} />
				)}

				{whatsApp.status === "qrcode" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<CropFree />
					</CustomToolTip>
				)}
				{whatsApp.status === "CONNECTED" && (
					<CustomToolTip title={i18n.t("connections.toolTips.CONNECTED.title")}>
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				)}

			</div>
		);
	};

	const renderCloudApiSubscription = (whatsApp) => {
		if (whatsApp.type !== "cloudapi") {
			return "-";
		}

		const subscriptionStatus = whatsApp.cloudApiSubscriptionStatus || "pending";
		const color = subscriptionStatus === "subscribed" ? green[500] : "#f57c00";
		const isSubscribed = subscriptionStatus === "subscribed";

		const label = (
			<Typography style={{ color, fontWeight: 600 }}>
				{isSubscribed ? i18n.t("connections.subscription.subscribed") : i18n.t("connections.subscription.pending")}
			</Typography>
		);

		// A inscrição é automática ao criar/salvar a conexão. Quando fica pendente
		// é porque a Meta recusou, e o motivo dela é o que resolve o problema —
		// sem isso "Pendente" não diz o que fazer.
		if (!isSubscribed && whatsApp.cloudApiSubscriptionLastError) {
			return (
				<Tooltip title={whatsApp.cloudApiSubscriptionLastError} arrow>
					<span style={{ cursor: "help" }}>{label}</span>
				</Tooltip>
			);
		}

		return label;
	};

	return (
		<MainContainer>
			<Dialog
				open={!!linkModalWhatsApp}
				onClose={handleCloseLinkModal}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{linkModalWhatsApp
						? i18n.t("connections.publicLink.titleWithName", { name: linkModalWhatsApp.name })
						: i18n.t("connections.publicLink.title")}
				</DialogTitle>
				<DialogContent>
					{linkLoading || !linkData ? (
						<div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
							<CircularProgress size={28} />
						</div>
					) : (
						<>
							<DialogContentText>
								{i18n.t("connections.publicLink.defaultLink")}
							</DialogContentText>
							<TextField
								value={linkData.url || ""}
								fullWidth
								size="small"
								variant="outlined"
								margin="dense"
								InputProps={{
									readOnly: true,
									endAdornment: (
										<InputAdornment position="end">
											<Tooltip title={i18n.t("connections.publicLink.copy")}>
												<IconButton
													size="small"
													onClick={() => handleCopyText(linkData.url, i18n.t("connections.toasts.publicLinkCopied"))}
												>
													<LinkIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</InputAdornment>
									),
								}}
							/>

							<DialogContentText style={{ marginTop: 16 }}>
								{i18n.t("connections.publicLink.nicknameHelp")}
							</DialogContentText>
							<TextField
								value={slugInput}
								onChange={e => setSlugInput(e.target.value)}
								fullWidth
								size="small"
								variant="outlined"
								margin="dense"
								placeholder={i18n.t("connections.publicLink.nicknamePlaceholder")}
								helperText={i18n.t("connections.publicLink.nicknameRules")}
							/>

							{linkData.customUrl ? (
								<>
									<DialogContentText style={{ marginTop: 8 }}>
										{i18n.t("connections.publicLink.activeLink")}
									</DialogContentText>
									<TextField
										value={linkData.customUrl}
										fullWidth
										size="small"
										variant="outlined"
										margin="dense"
										InputProps={{
											readOnly: true,
											endAdornment: (
												<InputAdornment position="end">
													<Tooltip title={i18n.t("connections.publicLink.copy")}>
														<IconButton
															size="small"
															onClick={() => handleCopyText(linkData.customUrl, i18n.t("connections.toasts.customLinkCopied"))}
														>
															<LinkIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</InputAdornment>
											),
										}}
									/>
								</>
							) : null}
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseLinkModal} color="secondary" disabled={linkSaving}>
						{i18n.t("common.close")}
					</Button>
					<Button
						onClick={handleSaveCustomLink}
						color="primary"
						variant="contained"
						disabled={linkLoading || linkSaving || !linkData}
					>
						{linkSaving ? <CircularProgress size={20} /> : i18n.t("connections.publicLink.saveNickname")}
					</Button>
				</DialogActions>
			</Dialog>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
				allowedConnectionType={whatsappConnectionType}
				allowQrConnection={allowQrConnection === true}
		  />
			{user?.super && (
				<ConnectionLogsModal
					open={!!logsModalWhatsApp}
					onClose={() => setLogsModalWhatsApp(null)}
					whatsAppId={logsModalWhatsApp?.id}
					whatsAppName={logsModalWhatsApp?.name}
				/>
			)}
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				<MainHeaderButtonsWrapper>
					{canManage && (
						<Button
							variant="contained"
							color="primary"
							onClick={handleOpenWhatsAppModal}
							disabled={allowQrConnection === null}
						>
							{i18n.t("connections.buttons.add")}
						</Button>
					)}
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<Paper className={classes.mainPaper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center">
								ID
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.name")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.number")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.type")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.status")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.subscription")}
							</TableCell>
							{canManage && (
								<TableCell align="center">
									{i18n.t("connections.table.session")}
								</TableCell>
							)}
							<TableCell align="center">
								{i18n.t("connections.table.lastUpdate")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.default")}
							</TableCell>
							{canManage && (
								<TableCell align="center">
									{i18n.t("connections.table.actions")}
								</TableCell>
							)}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton />
						) : (
							<>
								{filteredWhatsApps.length > 0 &&
									filteredWhatsApps
										.map(whatsApp => (
										<TableRow key={whatsApp.id}>
											<TableCell align="center">{whatsApp.id}</TableCell>
											<TableCell align="center" style={{ fontWeight: "bold", color: whatsApp.color || "inherit" }} onClick={e => handleConnectionNameClick(e, whatsApp)}>{whatsApp.name}</TableCell>										<TableCell align="center">{whatsApp.connectedNumber || "-"}</TableCell>											<TableCell align="center">
												{whatsApp.type === "cloudapi" ? (
													<Tooltip title={i18n.t("connections.typeTooltips.cloudapi")}>
														<Cloud style={{ color: "#25D366" }} />
													</Tooltip>
												) : whatsApp.type === "email" ? (
													<Tooltip title={i18n.t("connections.typeTooltips.email")}>
														<Email style={{ color: "#1976d2" }} />
													</Tooltip>
												) : whatsApp.type === "sms" ? (
													<Tooltip title={i18n.t("connections.typeTooltips.sms")}>
														<Sms style={{ color: "#F5A623" }} />
													</Tooltip>
												) : whatsApp.type === "instagram" ? (
													<Tooltip title={`Instagram${whatsApp.instagramUsername ? ` (@${whatsApp.instagramUsername})` : ""}`}>
														{whatsApp.instagramProfilePictureUrl ? (
															<Avatar
																src={whatsApp.instagramProfilePictureUrl}
																alt={whatsApp.instagramUsername || "Instagram"}
																style={{
																	width: 28,
																	height: 28,
																	margin: "0 auto",
																	border: "2px solid #E1306C",
																}}
															>
																<Instagram style={{ color: "#E1306C" }} />
															</Avatar>
														) : (
															<Instagram style={{ color: "#E1306C" }} />
														)}
													</Tooltip>
												) : (
													<Tooltip title={allowQrConnection ? i18n.t("connections.typeTooltips.baileys") : "WhatsApp"}>
														<PhoneAndroid style={{ color: "#128C7E" }} />
													</Tooltip>
												)}
											</TableCell>
											<TableCell align="center">
												{renderStatusToolTips(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{renderCloudApiSubscription(whatsApp)}
											</TableCell>
											{canManage && (
												<TableCell align="center">
													{renderActionButtons(whatsApp)}
												</TableCell>
											)}
											<TableCell align="center">
											{whatsApp.updatedAt ? format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm") : "-"}
											</TableCell>
											<TableCell align="center">
												{whatsApp.isDefault && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											{canManage && (
												<TableCell align="center">
													{/* Link público é a página de leitura do QR Code: só existe para o canal de QR */}
													{allowQrConnection && isQrChannel(whatsApp.type) && (
														<Tooltip title={i18n.t("connections.typeTooltips.publicLink")}>
															<IconButton
																size="small"
																onClick={() => handleOpenLinkModal(whatsApp)}
															>
																<LinkIcon />
															</IconButton>
														</Tooltip>
													)}

													<IconButton
														size="small"
														onClick={() => handleEditWhatsApp(whatsApp)}
													>
														<Edit />
													</IconButton>

													<IconButton
														size="small"
														onClick={e => {
															handleOpenConfirmationModal("delete", whatsApp.id, whatsApp.type);
														}}
													>
														<DeleteOutline />
													</IconButton>
												</TableCell>
											)}
										</TableRow>
									))}
							</>
						)}
					</TableBody>
				</Table>
			</Paper>
		</MainContainer>
	);
};

export default Connections;
